<template>
  <div v-if="activeDeal">
    <div class="deal-details-pane-container floating-pane overall-summary">
      <div class="floating-pane-header">
        <icon-deal />
        <span>{{ capitalize($t('messages.dealPreview')) }}</span>
        <div class="actions">
          <button
            class="btn-close-deal-details"
            @click="emit('clearActiveDeal')">
            <icon-close />
          </button>
        </div>
      </div>
      <div class="floating-pane-content">
        <div class="targeting-grid">
          <!-- DEAL NAME -->
          <div class="targeting-title">
            <div class="icon-xs"><icon-colored-deal /></div>
            <span>{{ capitalize($t('names.name')) }}</span>
          </div>
          <div class="targeting-value">
            {{ activeDeal.name }}
          </div>

          <!-- SUPPLIER -->
          <div class="targeting-title">
            <div class="icon-xs"><icon-colored-media-owner /></div>
            <span>{{ capitalize($t(isCuratedDeal ? 'names.curator' : 'names.mediaOwner', { n: 1 })) }}</span>
          </div>
          <div
            v-if="isCuratedDeal"
            class="targeting-value">
            <span>
              <img
                v-if="activeCurator.logoURL"
                v-tooltip="activeCurator.name"
                :src="activeCurator.logoURL"
                :alt="activeCurator.name" />
              <span v-else>{{ activeCurator.name }}</span>
            </span>
          </div>
          <div
            v-else
            class="targeting-value">
            <span
              v-for="id in activeDeal.mediaOwnerIDs"
              :key="id">
              <img
                v-if="getSupplierProperty(id, 'logoUrl')"
                v-tooltip="getSupplierProperty(id, 'name')"
                :src="getSupplierProperty(id, 'logoUrl')"
                :alt="getSupplierProperty(id, 'name')" />
              <span v-else>{{ getSupplierProperty(id, 'name') }}</span>
            </span>
          </div>

          <!-- CURATOR -->

          <!-- CPM -->
          <div class="targeting-title">
            <div class="icon-xs"><icon-colored-c-p-m></icon-colored-c-p-m></div>
            <span>{{ $t('names.cpm', { n: 1 }) }}</span>
          </div>
          <div class="targeting-value">
            {{ activeDeal.cpmDescription }}
          </div>

          <!-- CURRENCY -->
          <div class="targeting-title">
            <div class="icon-xs"><icon-colored-currency></icon-colored-currency></div>
            <span>{{ capitalize($t('names.currency', { n: 1 })) }}</span>
          </div>
          <div class="targeting-value">
            {{ activeCurrency }}
          </div>

          <!-- COUNTRY -->
          <div class="targeting-title">
            <div class="icon-xs"><icon-colored-country></icon-colored-country></div>
            <span>{{ capitalize($t('names.country', { n: 2 })) }}</span>
          </div>
          <div class="targeting-value">
            <span
              v-for="country in activeDeal.countries"
              :key="country">
              {{ country }}
            </span>
          </div>
        </div>

        <form-separator margin-size="xs">Targeting</form-separator>

        <!-- INVENTORY TARGETING -->
        <div class="targeting-grid">
          <DealInventoryTargetingDetails
            v-for="targeting in inventoryTargeting"
            :key="targeting.title"
            :targeting="targeting" />
          <div
            v-if="
              !inventoryTargeting.some((x) => x.isLoading) &&
              !inventoryTargeting.some((x) => x.included.length > 0 || x.excluded.length > 0)
            ">
            {{ capitalize($t('messages.allInventory')) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, markRaw, ref, watch } from 'vue';

  import FormSeparator from '@/common/components/controls/FormSeparator.vue';
  import DealInventoryTargetingDetails from '@/common/components/deals/DealInventoryTargetingDetails.vue';
  import {
    IconColoredCity,
    IconColoredCountry,
    IconColoredCPM,
    IconColoredCurrency,
    IconColoredDeal,
    IconColoredGeofenceGroup,
    IconColoredIab,
    IconColoredLanguage,
    IconColoredMarket,
    IconColoredMediaOwner,
    IconColoredMediaType,
    IconColoredOrientationLandscape,
    IconColoredRegion,
    IconColoredScreen,
    IconColoredSite,
    IconColoredVideo,
  } from '@/common/icons/colored';
  import { IconClose, IconDeal } from '@/common/icons/monochrome';
  import { GeofenceGroup, Language, Location, Market, Package, Site } from '@/common/models';
  import CreativeFormatOptions from '@/common/models/CreativeFormatOptions';
  import CreativeOrientationOptions from '@/common/models/CreativeOrientationOptions';
  import IABCategories from '@/common/models/iab-category/IABCategories';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import CuratorOrganization from '@/common/models/misc-api-models/CuratorOrganization';
  import { FilterCollection, QueryParams } from '@/common/models/query-params';
  import { mapActions, mapOrgAppState } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  interface IDealDetailsPaneProps {
    activeDeal: Package;
    suppliers: MediaOwner[];
    curators?: CuratorOrganization[];
  }

  const props = withDefaults(defineProps<IDealDetailsPaneProps>(), {
    activeDeal: null,
    suppliers: () => [],
    curators: () => [],
  });

  const emit = defineEmits(['clearActiveDeal']);

  const languages = ref<Language[]>([]);
  const locations = ref<Location[]>([]);
  const geofenceGroups = ref<GeofenceGroup[]>([]);
  const screens = ref<Screen[]>([]);
  const sites = ref<Site[]>([]);
  const markets = ref<Market[]>([]);
  const isLoadingLocations = ref(false);
  const isLoadingGeofenceGroups = ref(false);
  const isLoadingScreens = ref(false);
  const isLoadingSites = ref(false);
  const isLoadingMarkets = ref(false);
  const isLoadingLanguages = ref(false);

  const qpLocations = QueryParams.for({
    top: null,
    filterCollection: FilterCollection.for('location_id', []),
  });

  const qpGeofenceGroups = QueryParams.for({
    top: null,
    filterCollection: FilterCollection.for('geofencegroup_id', []),
  });

  const qpScreens = QueryParams.for({
    top: null,
    filterCollection: FilterCollection.for('unit_id', []),
  });

  const qpSites = QueryParams.for({
    top: null,
    filterCollection: FilterCollection.for('site_id', []),
  });

  const qpMarkets = QueryParams.for({
    top: null,
    filterCollection: FilterCollection.for('market_id', []),
  });

  const qpLanguages = QueryParams.for({
    top: null,
    filterCollection: FilterCollection.for('language_id', []),
  });

  const { ReferenceData } = mapOrgAppState();
  const { getLocations, getGeofenceGroups, getLanguages, getMarkets, getUnits, getSites } = mapActions();

  const isCuratedDeal = computed(() => props.activeDeal?.isCuratedDeal);

  const activeCurator = computed(() =>
    props.curators?.find((c) => c.curatorOrganizationID === props.activeDeal.organizationID),
  );

  const activeCurrency = computed(() => {
    const id = isCuratedDeal.value ? props.activeDeal?.mediaOwnerIDs[0] || -1 : props.activeDeal?.organizationID;
    return getSupplierProperty(id, 'currency');
  });

  const mediaTypesFlattened = computed(() => {
    const getChildren = (mt) => [mt, ...mt.children.map((x) => getChildren(x)).flat()];
    return ReferenceData.mediaTypes.map((mt) => getChildren(mt)).flat();
  });

  const activeMediaTypes = computed(() => {
    return mediaTypesFlattened.value.filter((x) => props.activeDeal?.mediaTypeIDs.includes(x.id));
  });

  const activeMediaTypeExclusions = computed(() => {
    return mediaTypesFlattened.value.filter((x) => props.activeDeal?.mediaTypeExclusionIDs.includes(x.id));
  });

  const activeMarkets = computed(() => {
    return markets.value?.filter((x) => props.activeDeal?.marketIDs.includes(x.id));
  });

  const activeMarketExclusions = computed(() => {
    return markets.value?.filter((x) => props.activeDeal?.marketExclusionIDs.includes(x.id));
  });

  const activeCities = computed(() => {
    return (
      locations.value?.filter((x) => x.level === 3).filter((x) => props.activeDeal?.locationIDs.includes(x.id)) || []
    );
  });

  const activeCityExclusions = computed(() => {
    return (
      locations.value
        ?.filter((x) => x.level === 3)
        .filter((x) => props.activeDeal?.locationExclusionIDs.includes(x.id)) || []
    );
  });

  const activeCountries = computed(() => {
    return (
      locations.value?.filter((x) => x.level === 0).filter((x) => props.activeDeal?.locationIDs.includes(x.id)) || []
    );
  });

  const activeCountryExclusions = computed(() => {
    return (
      locations.value
        ?.filter((x) => x.level === 0)
        .filter((x) => props.activeDeal?.locationExclusionIDs.includes(x.id)) || []
    );
  });

  const activeRegions = computed(() => {
    return (
      locations.value?.filter((x) => x.level === 1).filter((x) => props.activeDeal?.locationIDs.includes(x.id)) || []
    );
  });

  const activeRegionExclusions = computed(() => {
    return (
      locations.value
        ?.filter((x) => x.level === 1)
        .filter((x) => props.activeDeal?.locationExclusionIDs.includes(x.id)) || []
    );
  });

  const activeScreens = computed(() => {
    return screens.value?.filter((x) => props.activeDeal?.unitIDs.includes(x.id)) || [];
  });

  const activeScreenExclusions = computed(() => {
    return screens.value?.filter((x) => props.activeDeal?.unitExclusionIDs.includes(x.id)) || [];
  });

  const activeSites = computed(() => {
    return sites.value?.filter((x) => props.activeDeal?.siteIDs.includes(x.id)) || [];
  });

  const activeSiteExclusions = computed(() => {
    return sites.value?.filter((x) => props.activeDeal?.siteExclusionIDs.includes(x.id)) || [];
  });

  const activeGeofenceGroups = computed(() => {
    return geofenceGroups.value?.filter((x) => props.activeDeal?.geofenceGroupIDs.includes(x.id)) || [];
  });

  const activeGeofenceGroupExclusions = computed(() => {
    return geofenceGroups.value.filter((x) => props.activeDeal?.geofenceGroupExclusionIDs.includes(x.id)) || [];
  });

  const activeCreativeOrientations = computed(() => {
    return (
      CreativeOrientationOptions.options().filter((x) => props.activeDeal?.creativeOrientations.includes(x.id)) || []
    );
  });

  const activeCreativeOrientationExclusions = computed(() => {
    return (
      CreativeOrientationOptions.options().filter((x) =>
        props.activeDeal?.creativeOrientationExclusions.includes(x.id),
      ) || []
    );
  });

  const activeCreativeFormats = computed(() => {
    return CreativeFormatOptions.options().filter((x) => props.activeDeal?.creativeFormats.includes(x.id)) || [];
  });

  const activeCreativeFormatExclusions = computed(() => {
    return (
      CreativeFormatOptions.options().filter((x) => props.activeDeal?.creativeFormatExclusions.includes(x.id)) || []
    );
  });

  const activeLanguages = computed(() => {
    return languages.value?.filter((x) => props.activeDeal?.languageIDs.includes(x.id)) || [];
  });

  const activeLanguageExclusions = computed(() => {
    return languages.value?.filter((x) => props.activeDeal?.languageExclusionIDs.includes(x.id)) || [];
  });

  const activeIabCategories = computed(() => {
    return IABCategories.options().filter((x) => props.activeDeal?.iabCategories.includes(x.id)) || [];
  });

  const activeIabCategoryExclusions = computed(() => {
    return IABCategories.options().filter((x) => props.activeDeal?.iabCategoryExclusions.includes(x.id)) || [];
  });

  const inventoryTargeting = computed(() => {
    return [
      {
        title: capitalize(i18n.global.t('names.country', { n: 2 })),
        icon: markRaw(IconColoredCountry),
        isLoading: isLoadingLocations.value,
        included: activeCountries.value,
        excluded: activeCountryExclusions.value,
      },
      {
        title: capitalize(i18n.global.t('names.region', { n: 2 })),
        icon: markRaw(IconColoredRegion),
        isLoading: isLoadingLocations.value,
        included: activeRegions.value,
        excluded: activeRegionExclusions.value,
      },
      {
        title: capitalize(i18n.global.t('names.city', { n: 2 })),
        icon: markRaw(IconColoredCity),
        isLoading: isLoadingLocations.value,
        included: activeCities.value,
        excluded: activeCityExclusions.value,
      },
      {
        title: capitalize(i18n.global.t('names.market', { n: 2 })),
        icon: markRaw(IconColoredMarket),
        isLoading: isLoadingMarkets.value,
        included: activeMarkets.value,
        excluded: activeMarketExclusions.value,
      },
      {
        title: capitalize(i18n.global.t('names.venueType', { n: 2 })),
        icon: markRaw(IconColoredMediaType),
        included: activeMediaTypes.value,
        excluded: activeMediaTypeExclusions.value,
      },
      {
        title: capitalize(i18n.global.t('names.screen', { n: 2 })),
        icon: markRaw(IconColoredScreen),
        isLoading: isLoadingScreens.value,
        included: activeScreens.value,
        excluded: activeScreenExclusions.value,
      },
      {
        title: capitalize(i18n.global.t('names.site', { n: 2 })),
        icon: markRaw(IconColoredSite),
        isLoading: isLoadingSites.value,
        included: activeSites.value,
        excluded: activeSiteExclusions.value,
      },
      {
        title: capitalize(i18n.global.t('names.creativeOrientation', { n: 2 })),
        icon: markRaw(IconColoredOrientationLandscape),
        included: activeCreativeOrientations.value,
        excluded: activeCreativeOrientationExclusions.value,
      },
      {
        title: capitalize(i18n.global.t('names.creativeFormat', { n: 2 })),
        icon: markRaw(IconColoredVideo),
        included: activeCreativeFormats.value,
        excluded: activeCreativeFormatExclusions.value,
      },
      {
        title: capitalize(i18n.global.t('names.geofenceGroup', { n: 2 })),
        icon: markRaw(IconColoredGeofenceGroup),
        isLoading: isLoadingGeofenceGroups.value,
        included: activeGeofenceGroups.value,
        excluded: activeGeofenceGroupExclusions.value,
      },
      {
        title: capitalize(i18n.global.t('names.language', { n: 2 })),
        icon: markRaw(IconColoredLanguage),
        included: activeLanguages.value,
        excluded: activeLanguageExclusions.value,
      },
      {
        title: capitalize(i18n.global.t('names.category', { n: 2 })),
        icon: markRaw(IconColoredIab),
        included: activeIabCategories.value,
        excluded: activeIabCategoryExclusions.value,
      },
    ];
  });

  async function fetchLocations() {
    isLoadingLocations.value =
      props.activeDeal?.locationIDs?.length > 0 || props.activeDeal?.locationExclusionIDs?.length > 0;

    if (props.activeDeal?.locationIDs?.length > 0 || props.activeDeal?.locationExclusionIDs?.length) {
      try {
        qpLocations.filterCollection = FilterCollection.for('location_id', [
          ...(props.activeDeal?.locationIDs || []),
          ...(props.activeDeal?.locationExclusionIDs || []),
        ]);

        locations.value = (
          await getLocations({
            queryParams: qpLocations,
            cancelTokenSource: qpLocations.cancelTokenSource,
            showLoader: false,
          })
        ).items;
      } finally {
        isLoadingLocations.value = false;
      }
    }
  }
  async function fetchGeofenceGroups() {
    isLoadingGeofenceGroups.value =
      props.activeDeal?.geofenceGroupIDs?.length > 0 || props.activeDeal?.geofenceGroupExclusionIDs?.length > 0;

    if (props.activeDeal?.geofenceGroupIDs?.length > 0 || props.activeDeal?.geofenceGroupExclusionIDs?.length) {
      try {
        qpGeofenceGroups.filterCollection = FilterCollection.for('geofencegroup_id', [
          ...(props.activeDeal?.geofenceGroupIDs || []),
          ...(props.activeDeal?.geofenceGroupExclusionIDs || []),
        ]);

        geofenceGroups.value = (
          await getGeofenceGroups({
            queryParams: qpGeofenceGroups,
            cancelTokenSource: qpGeofenceGroups.cancelTokenSource,
            showLoader: false,
          })
        ).items;
      } finally {
        isLoadingGeofenceGroups.value = false;
      }
    }
  }

  async function fetchScreens() {
    isLoadingScreens.value = props.activeDeal?.unitIDs?.length > 0 || props.activeDeal?.unitExclusionIDs?.length > 0;

    if (props.activeDeal?.unitIDs?.length > 0 || props.activeDeal?.unitExclusionIDs?.length) {
      try {
        qpScreens.filterCollection = FilterCollection.for('unit_id', [
          ...(props.activeDeal?.unitIDs || []),
          ...(props.activeDeal?.unitExclusionIDs || []),
        ]);

        screens.value = (
          await getUnits({
            queryParams: qpScreens,
            cancelTokenSource: qpScreens.cancelTokenSource,
            showLoader: false,
          })
        ).items;
      } finally {
        isLoadingScreens.value = false;
      }
    }
  }

  async function fetchSites() {
    isLoadingSites.value = props.activeDeal?.siteIDs?.length > 0 || props.activeDeal?.siteExclusionIDs?.length > 0;

    if (props.activeDeal?.siteIDs?.length > 0 || props.activeDeal?.siteExclusionIDs?.length) {
      try {
        qpSites.filterCollection = FilterCollection.for('site_id', [
          ...(props.activeDeal?.siteIDs || []),
          ...(props.activeDeal?.siteExclusionIDs || []),
        ]);

        sites.value = (
          await getSites({
            queryParams: qpSites,
            cancelTokenSource: qpSites.cancelTokenSource,
            showLoader: false,
          })
        ).items;
      } finally {
        isLoadingSites.value = false;
      }
    }
  }

  async function fetchMarkets() {
    isLoadingMarkets.value =
      props.activeDeal?.marketIDs?.length > 0 || props.activeDeal?.marketExclusionIDs?.length > 0;

    if (props.activeDeal?.marketIDs?.length > 0 || props.activeDeal?.marketExclusionIDs?.length) {
      try {
        qpMarkets.filterCollection = FilterCollection.for('market_id', [
          ...(props.activeDeal?.marketIDs || []),
          ...(props.activeDeal?.marketExclusionIDs || []),
        ]);

        markets.value = (
          await getMarkets({
            queryParams: qpMarkets,
            cancelTokenSource: qpMarkets.cancelTokenSource,
            showLoader: false,
          })
        ).items;
      } finally {
        isLoadingMarkets.value = false;
      }
    }
  }

  async function fetchLanguages() {
    isLoadingLanguages.value =
      props.activeDeal?.languageIDs?.length > 0 || props.activeDeal?.languageExclusionIDs?.length > 0;

    if (props.activeDeal?.languageIDs?.length > 0 || props.activeDeal?.languageExclusionIDs?.length) {
      try {
        qpLanguages.filterCollection = FilterCollection.for('language_id', [
          ...(props.activeDeal?.languageIDs || []),
          ...(props.activeDeal?.languageExclusionIDs || []),
        ]);

        languages.value = (
          await getLanguages({
            queryParams: qpLanguages,
            cancelTokenSource: qpLanguages.cancelTokenSource,
            showLoader: false,
          })
        ).items;
      } finally {
        isLoadingLanguages.value = false;
      }
    }
  }

  function getSupplierProperty(id, prop) {
    return props.suppliers.find((s) => s.id === id)?.[prop];
  }

  watch(
    () => props.activeDeal,
    async () => {
      if (props.activeDeal == null) {
        return;
      }

      locations.value = [];
      geofenceGroups.value = [];
      screens.value = [];
      sites.value = [];
      markets.value = [];

      await Promise.all([
        fetchLocations(),
        fetchGeofenceGroups(),
        fetchScreens(),
        fetchSites(),
        fetchMarkets(),
        fetchLanguages(),
      ]);
    },
    { immediate: true },
  );
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;

  .icon-xs {
    display: flex;
    align-items: center;
  }

  .actions {
    button {
      display: flex;
      align-self: center;
      background-color: transparent;
      border: none;
      transition: all 0.2s ease;
      outline: none;
      :hover {
        cursor: pointer;
        filter: brightness(75%);
      }
    }
  }

  .floating-pane-header {
    svg {
      min-width: 1.6rem;
    }
  }
</style>
